import { z } from 'zod'

/** Runtime validation at the network boundary. */

// --- Primitives ---

/** Naive UTC: "2026-08-18 04:12:09". Validated as a STRING, never coerced to a */
export const naiveUtc = z.string().regex(/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}:\d{2}/, 'not a naive-UTC timestamp')
export const naiveDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/)

export const leadStatus = z.enum(['Pending', 'Resolved'])
export const leadPriority = z.enum(['Normal', 'Urgent'])
export const leadSource = z.enum(['lead_form', 'callback_confirm'])
export const messageRole = z.enum(['user', 'assistant'])

const pagination = {
  total: z.number().int(),
  limit: z.number().int(),
  offset: z.number().int(),
}

// --- Auth ---

export const roleSummarySchema = z.object({
  id: z.number().int(),
  slug: z.string(),
  name: z.string(),
  is_system: z.boolean(),
})

export const authUserSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  email: z.string(),
  role: roleSummarySchema,
  must_change_password: z.boolean(),
  /** Defined swatch name or null. */
  avatar_color: z.string().nullish(),
})

export const sessionInfoSchema = z.object({
  expires_at: naiveUtc,
  idle_expires_at: naiveUtc,
})

/** Login and `/auth/me` return the same shape by design. `me` is the bootstrap */
export const sessionResponseSchema = z.object({
  user: authUserSchema,
  // Permissions are told to the client so the UI can render itself. They.
  permissions: z.array(z.string()),
  csrf_token: z.string(),
  session: sessionInfoSchema,
})

/** `PATCH /api/auth/profile` — see docs/planning/16 §5. Not yet built server-side. */
export const profilePatchResponseSchema = z.object({
  user: authUserSchema,
})

export const changePasswordResponseSchema = z.object({
  ok: z.literal(true),
  csrf_token: z.string(),
  session: sessionInfoSchema,
})

// --- Leads ---

export const leadListRowSchema = z.object({
  id: z.number().int(),
  session_id: z.string(),
  name: z.string(),
  phone: z.string(),
  email: z.string().nullable(),
  issue_summary: z.string().nullable(),
  status: leadStatus,
  priority: leadPriority,
  source: leadSource,
  handoff_reason: z.string().nullable(),
  created_at: naiveUtc,
  first_page_url: z.string().nullable(),
  last_page_url: z.string().nullable(),
  // The same number legitimately raises several inquiries — phone verification
  inquiry_count: z.number().int(),
  // Explicit, so the UI can say "contact details are hidden for your role"
  masked: z.boolean(),
})

export const leadListResponseSchema = z.object({
  ...pagination,
  leads: z.array(leadListRowSchema),
})

export const transcriptTurnSchema = z.object({
  role: messageRole,
  content: z.string(),
  page_url: z.string().nullable().optional(),
  created_at: naiveUtc.nullable(),
})

/** No `source` — the contract does not carry it on a related row. */
export const relatedInquirySchema = z.object({
  id: z.number().int(),
  issue_summary: z.string().nullable(),
  status: leadStatus,
  priority: leadPriority,
  created_at: naiveUtc,
})

export const leadSessionSummarySchema = z.object({
  id: z.string(),
  created_at: naiveUtc,
  last_active: naiveUtc,
  first_page_url: z.string().nullable(),
  last_page_url: z.string().nullable(),
  handoff_requested: z.number().int(),
  handoff_reason: z.string().nullable(),
  message_count: z.number().int(),
})

/** Field names are the contract's, not convenient shorthands — see api-types.ts. */
export const leadDetailResponseSchema = z.object({
  lead: leadListRowSchema,
  session: leadSessionSummarySchema.nullable(),
  related_inquiries: z.array(relatedInquirySchema),
  /** null when the caller lacks chats.transcript.view — distinct from an empty chat. */
  transcript: z.array(transcriptTurnSchema).nullable(),
  transcript_truncated: z.boolean(),
})

export const leadPatchResponseSchema = z.object({ lead: leadListRowSchema })

// --- Stats ---

export const statsResponseSchema = z.object({
  leads: z.object({
    total: z.number().int(),
    pending: z.number().int(),
    urgent_open: z.number().int(),
    last_24h: z.number().int(),
    resolved_24h: z.number().int(),
  }),
  sessions: z.object({
    total: z.number().int(),
    active_24h: z.number().int(),
    today: z.number().int(),
  }),
  messages: z.object({
    total: z.number().int(),
    avg_per_session: z.number(),
  }),
  urgent_preview: z.array(leadListRowSchema),
})

export const trendResponseSchema = z.object({
  days: z.array(
    z.object({
      day: naiveDate,
      sessions: z.number().int(),
      leads: z.number().int(),
      urgent: z.number().int(),
    }),
  ),
})

// --- Health ---

/** `null` is "not configured" — a grey chip, never a red failure. */
const serviceOk = z.boolean().nullable()

export const healthServicesSchema = z.object({
  database: z.object({ ok: serviceOk, latency_ms: z.number().nullable(), version: z.string().nullable() }),
  chatbot: z.object({ ok: serviceOk, latency_ms: z.number().nullable(), version: z.string().nullable() }),
  ai_upstream: z.object({
    ok: serviceOk,
    provider: z.string().nullable(),
    model: z.string().nullable(),
    status: z.string(),
    checked_at: naiveUtc.nullable(),
  }),
  whatsapp: z.object({ ok: z.null(), status: z.literal('on_hold') }),
})

// --- Sessions ---

export const sessionListRowSchema = z.object({
  id: z.string(),
  created_at: naiveUtc,
  last_active: naiveUtc,
  first_page_url: z.string().nullable(),
  last_page_url: z.string().nullable(),
  handoff_requested: z.number().int(),
  message_count: z.number().int(),
  lead_count: z.number().int(),
})

export const sessionListResponseSchema = z.object({
  ...pagination,
  sessions: z.array(sessionListRowSchema),
})

export const sessionDetailResponseSchema = z.object({
  transcript_truncated: z.boolean().optional(),
  message_count: z.number().int().optional(),
  session: z.object({
    id: z.string(),
    created_at: naiveUtc,
    last_active: naiveUtc,
    first_page_url: z.string().nullable(),
    last_page_url: z.string().nullable(),
    handoff_requested: z.number().int(),
    handoff_reason: z.string().nullable(),
  }),
  messages: z.array(transcriptTurnSchema).nullable().optional(),
  leads: z.array(
    z.object({
      id: z.number().int(),
      name: z.string(),
      phone: z.string(),
      status: leadStatus,
      priority: leadPriority,
      masked: z.boolean(),
    }),
  ),
})

export type SessionResponseParsed = z.infer<typeof sessionResponseSchema>
export type LeadListRowParsed = z.infer<typeof leadListRowSchema>
export type StatsParsed = z.infer<typeof statsResponseSchema>

// --- Users, roles, permissions ---

export const userListRowSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  email: z.string(),
  role: roleSummarySchema,
  is_active: z.boolean(),
  must_change_password: z.boolean(),
  last_login_at: naiveUtc.nullable(),
  created_at: naiveUtc,
  override_count: z.number().int().optional(),
})

export const userListResponseSchema = z.object({
  ...pagination,
  users: z.array(userListRowSchema),
})

export const permissionOverrideSchema = z.object({
  key: z.string(),
  effect: z.enum(['grant', 'deny']),
  reason: z.string().nullable(),
  granted_by: z.string().nullable(),
  granted_at: naiveUtc,
})

/** All three layers separately — the Users screen must show WHY someone has something. */
export const userPermissionsSchema = z.object({
  role: z.object({
    id: z.number().int(),
    slug: z.string(),
    name: z.string(),
    permissions: z.array(z.string()),
  }),
  overrides: z.array(permissionOverrideSchema),
  effective: z.array(z.string()),
})

export const permissionsCatalogueSchema = z.object({
  groups: z.array(
    z.object({
      name: z.string(),
      permissions: z.array(
        z.object({
          key: z.string(),
          label: z.string(),
          description: z.string(),
          is_sensitive: z.boolean(),
        }),
      ),
    }),
  ),
})

export const roleListRowSchema = z.object({
  id: z.number().int(),
  slug: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  is_system: z.boolean(),
  permissions: z.array(z.string()),
  user_count: z.number().int(),
  created_at: naiveUtc,
})

export const roleListResponseSchema = z.object({ roles: z.array(roleListRowSchema) })
export const roleResponseSchema = z.object({ role: roleListRowSchema })

export const userCredentialsSchema = z.object({
  email: z.string(),
  password: z.string(),
  panel_url: z.string(),
})

export const userCreateResponseSchema = z.object({
  user: userListRowSchema,
  credentials: userCredentialsSchema,
})

// --- Settings ---

export const settingsSchema = z.object({
  ai: z.object({
    provider: z.string(),
    base_url: z.string(),
    key_present: z.boolean(),
    key_hint: z.string().nullable(),
    key_status: z.string(),
    key_checked_at: naiveUtc.nullable(),
    model: z.string(),
    model_source: z.enum(['settings', 'env']),
    enabled: z.boolean(),
  }),
  panel: z.object({
    session_idle_minutes: z.number().int(),
    session_absolute_hours: z.number().int(),
  }),
  leads: z.object({ retention_days: z.number().int().nullable() }),
  whatsapp: z.object({ status: z.literal('on_hold') }),
})

// --- Audit ---

export const auditRowSchema = z.object({
  id: z.number().int(),
  user_id: z.number().int().nullable(),
  user_email: z.string(),
  action: z.string(),
  resource_type: z.string(),
  resource_id: z.string().nullable(),
  detail: z.unknown().nullable(),
  ip: z.string().nullable(),
  created_at: naiveUtc,
})

export const auditListResponseSchema = z.object({
  ...pagination,
  entries: z.array(auditRowSchema),
})

export type UserListRowParsed = z.infer<typeof userListRowSchema>
export type RoleListRowParsed = z.infer<typeof roleListRowSchema>
export type PermissionsCatalogue = z.infer<typeof permissionsCatalogueSchema>
export type UserPermissions = z.infer<typeof userPermissionsSchema>
export type SettingsParsed = z.infer<typeof settingsSchema>
export type AuditRowParsed = z.infer<typeof auditRowSchema>

export const aiModelSchema = z.object({
  id: z.string(),
  label: z.string(),
  cost_per_conversation_inr: z.number(),
  /** Only models that scored 6/6 on the safety benchmark are offered at all. */
  safety_score: z.string(),
  recommended: z.boolean(),
})

export const aiModelsResponseSchema = z.object({ models: z.array(aiModelSchema) })

export const aiUsageDaySchema = z.object({
  day: naiveDate,
  model: z.string(),
  requests: z.number().int(),
  prompt_tokens: z.number().int(),
  /** Tracked apart from billed tokens: ~92% of input returns cached, so a figure
   *  that ignores it overstates spend by an order of magnitude. */
  cached_tokens: z.number().int(),
  completion_tokens: z.number().int(),
  errors: z.number().int(),
})

export const aiUsageResponseSchema = z.object({ days: z.array(aiUsageDaySchema) })

export const aiTestResponseSchema = z.object({
  ok: z.boolean(),
  latency_ms: z.number().nullable(),
  model: z.string().nullable(),
  error: z.string().nullable().optional(),
})

export type AiModelParsed = z.infer<typeof aiModelSchema>
export type AiUsageParsed = z.infer<typeof aiUsageResponseSchema>
